import { useCallback } from 'react';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export const usePdf = (clientName, targetMarket) => {
  const generatePDF = useCallback(
    async (ref) => {
      if (ref.current === null) {
        return;
      }

      const [header, content, footer] = [...ref.current.children];
      const coverPageHeight = header.children[0].clientHeight;
      const tablePageHeight = header.children[1].clientHeight;
      const scaleFactor = 2;
      const initialPageHeight = (coverPageHeight + tablePageHeight) / scaleFactor;
      const defaultPageHeight = 900;
      const pageWidth = 595;
      const topMargin = 15; // Top margin for second and subsequent pages of large content
      const bottomMargin = 43; // Bottom margin for all pages of large content

      const pdf = new jsPDF('p', 'pt', [pageWidth, initialPageHeight]);

      const findSafeCuttingPoint = (canvas, startHeight, segmentHeight, backgroundColor) => {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const imageData = ctx.getImageData(0, startHeight, canvas.width, segmentHeight).data;

        for (let y = segmentHeight - 1; y >= 0; y--) {
          let differentFromBackgroundCount = 0;

          for (let x = 0; x < canvas.width; x++) {
            const index = (y * canvas.width + x) * 4;
            const r = imageData[index];
            const g = imageData[index + 1];
            const b = imageData[index + 2];

            // Assuming background color is known and provided as backgroundColor = {r, g, b}
            if (
              Math.abs(r - backgroundColor.r) > 10 ||
              Math.abs(g - backgroundColor.g) > 10 ||
              Math.abs(b - backgroundColor.b) > 10
            ) {
              // Adjust the threshold as needed
              differentFromBackgroundCount++;
            }
          }

          // If a significant number of pixels are different from the background, this might be a text line
          if (differentFromBackgroundCount > canvas.width * 0.1) {
            // Adjust the percentage as needed
            return startHeight + y; // Return the position just before this line
          }
        }

        return startHeight + segmentHeight; // No text detected, safe to cut at the end of the segment
      };

      const canvasArray = [];

      canvasArray.push(html2canvas(header, { useCORS: true }));
      for (let i = 0; i < content.children.length; i++) {
        const options = content.children[i].firstChild.children;

        for (let j = 0; j < options.length; j++) {
          canvasArray.push(html2canvas(options[j], { useCORS: true, width: 1400 }));
        }
      }
      canvasArray.push(html2canvas(footer));

      await Promise.all(canvasArray)
        .then((canvasArray) => {
          canvasArray.forEach((canvas, index) => {
            const isContent = index !== 0 && index !== canvasArray.length - 1; // True for content, false for header/footer
            const ratio = canvas.width / pageWidth;
            let currentHeight = 0;
            let isFirstSegmentOfCanvas = true;

            while (currentHeight < canvas.height) {
              // const pageHeight = isFirstSegmentOfCanvas
              //   ? initialPageHeight
              //   : defaultPageHeight - bottomMargin;

              const pageHeight =
                index === 0 && currentHeight === 0
                  ? initialPageHeight
                  : defaultPageHeight - bottomMargin;

              let segmentHeight = Math.min(canvas.height - currentHeight, pageHeight * ratio);
              const backgroundColor = { r: 255, g: 255, b: 255 }; // Background color for safe cut

              if (isContent && currentHeight + segmentHeight + bottomMargin < canvas.height) {
                segmentHeight = Math.max(segmentHeight - bottomMargin, 1);
                const safeCuttingPoint = findSafeCuttingPoint(
                  canvas,
                  currentHeight,
                  segmentHeight,
                  backgroundColor,
                );

                segmentHeight = safeCuttingPoint - currentHeight;
              }

              const dHeight = segmentHeight / ratio;
              const canvasEle = document.createElement('canvas');

              canvasEle.width = canvas.width;
              canvasEle.height = segmentHeight;
              const ctx = canvasEle.getContext('2d');

              ctx.drawImage(
                canvas,
                0,
                currentHeight,
                canvas.width,
                segmentHeight,
                0,
                0,
                canvas.width,
                segmentHeight,
              );

              if (currentHeight !== 0 || index !== 0) {
                pdf.addPage([pageWidth, defaultPageHeight - bottomMargin], 'p', 'pt');
              }

              const yPos = isContent && !isFirstSegmentOfCanvas ? topMargin : 0;

              pdf.addImage(
                canvasEle.toDataURL('image/jpeg', 1.0),
                'JPEG',
                0,
                yPos,
                pageWidth,
                dHeight,
              );

              currentHeight += segmentHeight;
              isFirstSegmentOfCanvas = false;
            }
          });

          const pdfName = `CBRE Agile_${clientName}_${targetMarket}.pdf`;

          pdf.save(pdfName);
        })
        .catch((err) => console.log(err));
    },
    [clientName, targetMarket],
  );

  return { generatePDF };
};
